'use client';

/**
 * ToolTimeoutRetrying — "trying again" card of the tool-timeout recovery
 * flow (lib/tool-timeout-view.ts's ToolTimeoutView.RETRYING), shown while
 * the container's actual retry call is in flight. Purely presentational —
 * the container decides when this mounts/unmounts based on the real retry
 * outcome (RETRY_SUCCEEDED -> ToolTimeoutRecovered, RETRY_FAILED ->
 * ToolTimeoutFailed).
 *
 * The three-dot pulse mirrors typing-indicator.tsx's existing visual
 * technique (staggered animate-bounce dots) rather than importing that
 * component directly — TypingIndicator is a full assistant-bubble shell for
 * a different context (the message list's "Sara is composing" state), not
 * an inline title-row accent. Gated behind motion-safe: (typing-indicator.tsx
 * itself doesn't respect prefers-reduced-motion; this component does, per
 * this task's explicit requirement).
 *
 * Copy review: used to take a `toolLabel` prop and render "Reaching
 * {toolLabel}..." — a mechanism noun ("reaching" a named internal
 * service) exposed straight to the user. Dropped `toolLabel` entirely
 * (see tool-timeout-offer.tsx's own copy-review note for the full
 * `tool_label` decision) in favor of a generic "Still looking…", which
 * also fixes an unrelated punctuation slip this line was the last holdout
 * of: every other in-progress label in this codebase uses the real
 * ellipsis character ("Sending…", "Connecting…"), not three typed periods.
 */
import { RefreshCw } from 'lucide-react';
import { STANDARD_CARD_TONE, StandardCard } from './primitives/standard-card';

interface ToolTimeoutRetryingProps {
  onSkip: () => void;
}

function TitleDots() {
  return (
    <span
      className="inline-flex items-center gap-[3px] pl-[2px]"
      aria-hidden="true"
    >
      <span
        className="size-[4px] rounded-full motion-safe:animate-bounce"
        style={{
          backgroundColor: '#9598a6',
          animationDelay: '0ms',
          animationDuration: '1s',
        }}
      />
      <span
        className="size-[4px] rounded-full motion-safe:animate-bounce"
        style={{
          backgroundColor: '#9598a6',
          animationDelay: '150ms',
          animationDuration: '1s',
        }}
      />
      <span
        className="size-[4px] rounded-full motion-safe:animate-bounce"
        style={{
          backgroundColor: '#9598a6',
          animationDelay: '300ms',
          animationDuration: '1s',
        }}
      />
    </span>
  );
}

export function ToolTimeoutRetrying({ onSkip }: ToolTimeoutRetryingProps) {
  return (
    <StandardCard
      tone={STANDARD_CARD_TONE.NEUTRAL}
      icon={
        <RefreshCw
          aria-hidden="true"
          className="size-[15px] motion-safe:animate-spin"
        />
      }
      title={
        <span className="inline-flex items-center">
          Trying again
          <TitleDots />
        </span>
      }
      actions={{
        secondary: {
          label: 'Skip & leave details',
          variant: 'quiet',
          onClick: onSkip,
        },
      }}
    >
      Still looking…
    </StandardCard>
  );
}
